import type {
  MeAnnouncementItem,
  MeAnnouncementsList,
  MeBenefit,
  MeBenefitListResponse,
  MeCorrectionRequest,
  MeDashboard,
  MeDocument,
  MeDocumentContentResult,
  MeDocumentDownload,
  MeDocumentListQuery,
  MeDocumentListResponse,
  MePayslipDetail,
  MePayslipDownloadResult,
  MePayslipListResponse,
  MeProfile,
  MeTimecardBalance,
  MeTimecardListQuery,
  MeTimecardListResponse,
  MeVacationBalance,
  MeVacationHistory,
  PatchMeProfileBody,
  RequestMeCorrectionBody,
  RevealMeFieldResult,
  WorkflowInstance,
} from "@evolua/api-client";

/**
 * Portal do colaborador — fixtures para VITE_AUTH_MODE=demo (FASE-06-T32..T35).
 * Espelha `apps/api-core/src/modules/me/me.repository.ts` (Ana, id ...111).
 */

const ANA_ID = "11111111-1111-4111-8111-111111111111";

type PayslipSeed = MePayslipDetail;

const PAYSLIPS: PayslipSeed[] = [
  {
    id: "payslip-2026-07",
    competence: "2026-07",
    grossAmount: 6200,
    discounts: 1379.45,
    netAmount: 4820.55,
    availableAt: "2026-08-05T12:00:00.000Z",
    syncedAt: "2026-08-05T12:05:00.000Z",
    auditId: "",
    lines: [
      { label: "Salário base", type: "EARNING", amount: 6200 },
      { label: "INSS", type: "DEDUCTION", amount: 663.45 },
      { label: "IRRF", type: "DEDUCTION", amount: 416 },
      { label: "Vale-transporte", type: "DEDUCTION", amount: 300 },
    ],
  },
  {
    id: "payslip-2026-06",
    competence: "2026-06",
    grossAmount: 6150,
    discounts: 1359.9,
    netAmount: 4790.1,
    availableAt: "2026-07-05T12:00:00.000Z",
    syncedAt: "2026-07-05T12:05:00.000Z",
    auditId: "",
    lines: [
      { label: "Salário base", type: "EARNING", amount: 6150 },
      { label: "INSS", type: "DEDUCTION", amount: 658.9 },
      { label: "IRRF", type: "DEDUCTION", amount: 401 },
      { label: "Vale-transporte", type: "DEDUCTION", amount: 300 },
    ],
  },
  {
    id: "payslip-2026-05",
    competence: "2026-05",
    grossAmount: 6150,
    discounts: 1359.9,
    netAmount: 4790.1,
    availableAt: "2026-06-05T12:00:00.000Z",
    syncedAt: "2026-06-05T12:05:00.000Z",
    auditId: "",
    lines: [
      { label: "Salário base", type: "EARNING", amount: 6150 },
      { label: "INSS", type: "DEDUCTION", amount: 658.9 },
      { label: "IRRF", type: "DEDUCTION", amount: 401 },
      { label: "Vale-transporte", type: "DEDUCTION", amount: 300 },
    ],
  },
];

const VACATION_BALANCE: MeVacationBalance = {
  periods: [
    {
      id: "vacation-period-1",
      label: "Período aquisitivo 2025/2026",
      acquisitiveStart: "2025-01-15",
      acquisitiveEnd: "2026-01-14",
      balanceDays: 18,
      limitDate: "2027-01-15",
      status: "OPEN",
    },
  ],
  totalBalanceDays: 18,
  syncedAt: "2026-08-10T09:00:00.000Z",
};

const VACATION_HISTORY: MeVacationHistory = {
  data: [
    {
      id: "vacation-history-2",
      type: "SCHEDULED",
      startDate: "2026-12-07",
      endDate: "2026-12-21",
      days: 15,
      status: "APPROVED",
    },
    {
      id: "vacation-history-1",
      type: "TAKEN",
      startDate: "2025-06-02",
      endDate: "2025-06-16",
      days: 15,
      status: "COMPLETED",
    },
  ],
  syncedAt: "2026-08-10T09:00:00.000Z",
};

const ANNOUNCEMENTS: MeDashboard["announcements"] = [
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa11",
    title: "Novo plano de saúde a partir de setembro",
    body: "A partir de 01/09 o plano de saúde passa a incluir odontologia sem custo adicional.",
    publishedAt: "2026-08-08T10:00:00.000Z",
  },
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa12",
    title: "Recesso de fim de ano",
    body: "O escritório fecha entre 24/12 e 01/01. Ajuste suas solicitações de férias com antecedência.",
    publishedAt: "2026-08-01T10:00:00.000Z",
  },
];

const DEMO_ANNOUNCEMENTS: MeAnnouncementItem[] = [
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa11",
    title: "Novo plano de saúde a partir de setembro",
    body: "A partir de 01/09 o plano de saúde passa a incluir odontologia sem custo adicional.",
    publishedAt: "2026-08-08T10:00:00.000Z",
    readAt: null,
    ackedAt: null,
    status: "UNREAD",
  },
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa12",
    title: "Recesso de fim de ano",
    body: "O escritório fecha entre 24/12 e 01/01. Ajuste suas solicitações de férias com antecedência.",
    publishedAt: "2026-08-01T10:00:00.000Z",
    readAt: "2026-08-02T12:00:00.000Z",
    ackedAt: null,
    status: "READ",
  },
  {
    id: "cccccccc-cccc-4ccc-8ccc-cccccccccc01",
    title: "Campanha de vacinação 2026",
    body: "A vacinação contra a gripe ocorre na semana de 18/08 no escritório. Confirme ciência.",
    publishedAt: "2026-08-10T12:00:00.000Z",
    readAt: null,
    ackedAt: null,
    status: "UNREAD",
  },
];

function statusOf(row: MeAnnouncementItem): MeAnnouncementItem["status"] {
  if (row.ackedAt) return "ACKED";
  if (row.readAt) return "READ";
  return "UNREAD";
}

type PendingItem = MeDashboard["pendingItems"][number];

const PENDING_ITEMS: PendingItem[] = [
  {
    id: "pending-1",
    type: "VACATION_REQUEST",
    label: "Férias de dezembro aguardando aprovação do gestor",
    status: "PENDING_APPROVAL",
    dueDate: "2026-12-07",
  },
];

const SECRETS: Record<"cpf" | "bankAccount" | "salaryAmount", string> = {
  cpf: "123.456.789-00",
  bankAccount: "1234-5 / 98765-1",
  salaryAmount: "6200",
};

type ProfileState = {
  displayName: string | null;
  email: string | null;
  emailFromErp: boolean;
  version: number;
  history: MeProfile["fieldHistory"];
};

const PROFILE_STATE: ProfileState = {
  displayName: "Ana",
  email: null,
  emailFromErp: true,
  version: 1,
  history: [],
};

const CORRECTIONS: MeCorrectionRequest[] = [];

const PROFILE_FIELD_ORIGINS: MeProfile["fieldOrigins"] = [
  { field: "fullName", ownership: "ERP" },
  { field: "registrationNumber", ownership: "ERP" },
  { field: "cpf", ownership: "ERP" },
  { field: "bankAccount", ownership: "ERP" },
  { field: "status", ownership: "ERP" },
  { field: "hireDate", ownership: "ERP" },
  { field: "departmentName", ownership: "ERP" },
  { field: "positionName", ownership: "ERP" },
  { field: "email", ownership: "HYBRID" },
  { field: "displayName", ownership: "PLATFORM" },
  { field: "syncedAt", ownership: "PLATFORM" },
];

function buildProfile(): MeProfile {
  return {
    employeeId: ANA_ID,
    fullName: "Ana Souza",
    email: PROFILE_STATE.email ?? "ana.souza@demo.local",
    emailEditable: !PROFILE_STATE.emailFromErp,
    displayName: PROFILE_STATE.displayName,
    registrationNumber: "00001",
    departmentName: "Operações",
    positionName: "Analista",
    hireDate: "2021-03-15",
    status: "ACTIVE",
    cpf: "***.***.***-00",
    bankAccount: "****-00",
    version: PROFILE_STATE.version,
    syncedAt: "2026-08-11T12:00:00.000Z",
    fieldOrigins: PROFILE_FIELD_ORIGINS,
    fieldHistory: [...PROFILE_STATE.history].reverse(),
  };
}

export function getDemoMeDashboard(): MeDashboard {
  const lastPayslip = PAYSLIPS[0] ?? null;
  const period = VACATION_BALANCE.periods[0] ?? null;
  return {
    employeeId: ANA_ID,
    displayName: PROFILE_STATE.displayName ?? "Ana",
    lastPayslip: lastPayslip
      ? {
          id: lastPayslip.id,
          competence: lastPayslip.competence,
          netAmount: lastPayslip.netAmount,
          availableAt: lastPayslip.availableAt,
        }
      : null,
    vacationBalance: period
      ? {
          balanceDays: VACATION_BALANCE.totalBalanceDays,
          limitDate: period.limitDate,
          syncedAt: VACATION_BALANCE.syncedAt ?? "",
        }
      : null,
    pendingItems: [...PENDING_ITEMS],
    announcements: [...ANNOUNCEMENTS],
    syncedAt: lastPayslip?.syncedAt ?? VACATION_BALANCE.syncedAt ?? null,
    links: {
      profile: "/perfil",
      payslips: "/holerite",
      vacations: "/ferias",
      benefits: "/beneficios",
      documents: "/documentos",
    },
  };
}

export function getDemoMeProfile(): MeProfile {
  return buildProfile();
}

export function patchDemoMeProfile(body: PatchMeProfileBody): MeProfile {
  if (body.expectedVersion !== PROFILE_STATE.version) {
    throw new Error("VERSION_CONFLICT");
  }
  const auditId = crypto.randomUUID();
  const at = new Date().toISOString();
  if (body.displayName !== undefined && body.displayName !== PROFILE_STATE.displayName) {
    PROFILE_STATE.history.push({
      id: crypto.randomUUID(),
      field: "displayName",
      previous: PROFILE_STATE.displayName,
      next: body.displayName,
      at,
      auditId,
    });
    PROFILE_STATE.displayName = body.displayName;
  }
  if (body.email !== undefined && !PROFILE_STATE.emailFromErp) {
    PROFILE_STATE.history.push({
      id: crypto.randomUUID(),
      field: "email",
      previous: PROFILE_STATE.email,
      next: body.email,
      at,
      auditId,
    });
    PROFILE_STATE.email = body.email;
  }
  PROFILE_STATE.version += 1;
  return buildProfile();
}

export function revealDemoMeField(
  field: "cpf" | "bankAccount" | "salaryAmount",
): RevealMeFieldResult {
  return { field, value: SECRETS[field], auditId: crypto.randomUUID() };
}

export function requestDemoMeCorrection(
  body: RequestMeCorrectionBody,
): MeCorrectionRequest {
  const auditId = crypto.randomUUID();
  const request: MeCorrectionRequest = {
    id: crypto.randomUUID(),
    field: body.field,
    currentValue: body.currentValue ?? null,
    requestedValue: body.requestedValue,
    reason: body.reason,
    status: "OPEN",
    createdAt: new Date().toISOString(),
    auditId,
  };
  CORRECTIONS.unshift(request);
  PENDING_ITEMS.push({
    id: crypto.randomUUID(),
    type: "CORRECTION_REQUEST",
    label: `Correção solicitada: ${body.field}`,
    status: "OPEN",
    dueDate: null,
  });
  return request;
}

export function listDemoMeCorrections(): { data: MeCorrectionRequest[] } {
  return { data: [...CORRECTIONS] };
}

export function listDemoMePayslips(): MePayslipListResponse {
  return {
    data: PAYSLIPS.map(
      ({ id, competence, grossAmount, netAmount, availableAt, syncedAt }) => ({
        id,
        competence,
        grossAmount,
        netAmount,
        availableAt,
        syncedAt,
      }),
    ),
  };
}

export function getDemoMePayslip(id: string): MePayslipDetail {
  const payslip = PAYSLIPS.find((p) => p.id === id);
  if (!payslip) throw new Error("Holerite não encontrado (demo)");
  return { ...payslip, auditId: crypto.randomUUID() };
}

export function downloadDemoMePayslip(id: string): MePayslipDownloadResult {
  const payslip = getDemoMePayslip(id);
  const lines = [
    "Holerite — Ana Souza",
    `Competência: ${payslip.competence}`,
    "",
    ...payslip.lines.map(
      (l) => `${l.type === "EARNING" ? "+" : "-"} ${l.label}: ${l.amount.toFixed(2)}`,
    ),
    "",
    `Líquido: ${payslip.netAmount.toFixed(2)}`,
    "",
    "Documento mock-first (FASE-06-T34, demo) — piloto ERP real gera PDF assinado (P-001).",
  ];
  return {
    filename: `holerite-${payslip.competence}.txt`,
    content: lines.join("\n"),
    auditId: crypto.randomUUID(),
  };
}

export function getDemoMeVacationBalance(): MeVacationBalance {
  return VACATION_BALANCE;
}

export function getDemoMeVacationHistory(): MeVacationHistory {
  return VACATION_HISTORY;
}

const VACATION_REQUESTS: WorkflowInstance[] = [];

export function listDemoMeVacationRequests(): { data: WorkflowInstance[] } {
  return { data: [...VACATION_REQUESTS].reverse() };
}

export function requestDemoMeVacation(body: {
  startDate: string;
  endDate: string;
  reason?: string;
}): WorkflowInstance {
  const start = Date.parse(`${body.startDate}T00:00:00.000Z`);
  const end = Date.parse(`${body.endDate}T00:00:00.000Z`);
  const days = Math.round((end - start) / 86_400_000) + 1;
  const balance = VACATION_BALANCE.periods.reduce((a, p) => a + p.balanceDays, 0);
  if (days > balance) throw new Error("VACATION_EXCEEDS_BALANCE");
  if (start < Date.now() + 7 * 86_400_000) throw new Error("VACATION_MIN_ADVANCE");
  for (const existing of VACATION_REQUESTS.filter((r) => r.status === "IN_PROGRESS")) {
    const a = String(existing.payload.startDate ?? "");
    const b = String(existing.payload.endDate ?? "");
    if (body.startDate <= b && body.endDate >= a) throw new Error("VACATION_OVERLAP");
  }
  const now = new Date().toISOString();
  const row = {
    id: crypto.randomUUID(),
    type: "VACATION",
    status: "IN_PROGRESS" as const,
    requesterSubject: "demo-employee",
    subjectEmployeeId: "11111111-1111-4111-8111-111111111111",
    payload: {
      startDate: body.startDate,
      endDate: body.endDate,
      days,
      reason: body.reason ?? null,
    },
    currentStepIndex: 0,
    createdAt: now,
    updatedAt: now,
    completedAt: null,
    steps: [
      {
        key: "manager-approval",
        name: "Aprovação do gestor",
        status: "ACTIVE" as const,
        assignedSubjects: ["demo-manager"],
        slaHours: 48,
        dueAt: null,
        decidedBy: null,
        decidedAt: null,
        comment: null,
        escalated: false,
        escalatedAt: null,
        escalatedFrom: null,
        approver: { kind: "DIRECT_MANAGER" as const },
      },
    ],
  } as unknown as WorkflowInstance;
  VACATION_REQUESTS.push(row);
  return row;
}

export function cancelDemoMeVacationRequest(id: string): WorkflowInstance {
  const row = VACATION_REQUESTS.find((r) => r.id === id);
  if (!row) throw new Error("Solicitação não encontrada");
  if (row.status !== "IN_PROGRESS") throw new Error("WORKFLOW_ALREADY_CLOSED");
  (row as unknown as { status: string }).status = "CANCELLED";
  row.completedAt = new Date().toISOString();
  row.updatedAt = row.completedAt;
  row.currentStepIndex = null;
  return row;
}

const DEMO_BENEFITS: MeBenefit[] = [
  {
    id: "benefit-health",
    name: "Plano de saúde",
    type: "HEALTH",
    category: "SAUDE",
    status: "ACTIVE",
    description: "Plano empresarial com cobertura nacional e odontologia.",
    attributes: { operadora: "Unimed", plano: "Empresarial Plus", titular: "Ana Silva" },
    validFrom: "2024-01-01",
    validTo: null,
  },
  {
    id: "benefit-meal",
    name: "Vale-refeição",
    type: "MEAL",
    category: "ALIMENTACAO",
    status: "ACTIVE",
    description: "Crédito mensal em cartão de refeição.",
    attributes: { valorMensal: "R$ 880,00", bandeira: "VR" },
    validFrom: "2024-01-01",
    validTo: null,
  },
  {
    id: "benefit-transport",
    name: "Vale-transporte",
    type: "TRANSPORT",
    category: "MOBILIDADE",
    status: "ACTIVE",
    description: "Desconto em folha conforme uso declarado.",
    attributes: { modalidade: "Bilhete único" },
    validFrom: "2024-01-01",
    validTo: null,
  },
];

const DEMO_DOCUMENTS: Array<MeDocument & { content: string }> = [
  {
    id: "doc-contract",
    name: "Contrato de trabalho",
    category: "ADMISSION",
    issuedAt: "2026-08-10",
    expiresAt: null,
    filename: "contrato-trabalho.txt",
    content: "Contrato de trabalho — Ana Silva (demo).\n",
  },
  {
    id: "doc-policy",
    name: "Política de férias",
    category: "POLICY",
    issuedAt: "2026-01-15",
    expiresAt: null,
    filename: "politica-ferias.txt",
    content: "Política interna de férias (demo).\n",
  },
  {
    id: "doc-exam",
    name: "ASO admissional",
    category: "HEALTH",
    issuedAt: "2026-08-08",
    expiresAt: "2027-08-08",
    filename: "aso-admissional.txt",
    content: "ASO admissional — apto (demo).\n",
  },
];

const demoDocTokens = new Map<string, string>();

export function listDemoMeBenefits(): MeBenefitListResponse {
  return {
    data: DEMO_BENEFITS.map((b) => ({ ...b })),
    total: DEMO_BENEFITS.length,
    capabilityAvailable: true,
  };
}

export function getDemoMeBenefit(id: string): MeBenefit {
  const found = DEMO_BENEFITS.find((b) => b.id === id);
  if (!found) throw new Error("BENEFIT_NOT_FOUND");
  return { ...found, auditId: `audit-${id}` };
}

export function listDemoMeDocuments(
  query: MeDocumentListQuery = {},
): MeDocumentListResponse {
  let data = DEMO_DOCUMENTS.map(({ content: _c, ...rest }) => ({ ...rest }));
  if (query.category) data = data.filter((d) => d.category === query.category);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (d) => d.name.toLowerCase().includes(q) || d.category.toLowerCase().includes(q),
    );
  }
  return {
    data,
    total: data.length,
    categories: [...new Set(DEMO_DOCUMENTS.map((d) => d.category))].sort(),
    capabilityAvailable: true,
  };
}

export function getDemoMeDocument(id: string): MeDocument {
  const found = DEMO_DOCUMENTS.find((d) => d.id === id);
  if (!found) throw new Error("DOCUMENT_NOT_FOUND");
  const { content: _c, ...rest } = found;
  return { ...rest, auditId: `audit-${id}` };
}

export function createDemoMeDocumentDownload(id: string): MeDocumentDownload {
  const found = DEMO_DOCUMENTS.find((d) => d.id === id);
  if (!found) throw new Error("DOCUMENT_NOT_FOUND");
  const token = `demo-token-${id}-${Date.now()}`;
  demoDocTokens.set(token, id);
  return {
    downloadUrl: `/v1/me/documents/${id}/content?token=${token}`,
    expiresAt: new Date(Date.now() + 60_000).toISOString(),
    filename: found.filename,
    auditId: `audit-dl-${id}`,
  };
}

export function downloadDemoMeDocumentContent(
  downloadUrl: string,
): MeDocumentContentResult {
  const token = new URL(downloadUrl, "http://local").searchParams.get("token") ?? "";
  const id = demoDocTokens.get(token);
  if (!id) throw new Error("DOCUMENT_DOWNLOAD_EXPIRED");
  demoDocTokens.delete(token);
  const found = DEMO_DOCUMENTS.find((d) => d.id === id);
  if (!found) throw new Error("DOCUMENT_NOT_FOUND");
  return { filename: found.filename, content: found.content };
}

export function listDemoMeAnnouncements(): MeAnnouncementsList {
  const data = DEMO_ANNOUNCEMENTS.map((a) => ({
    ...a,
    status: statusOf(a),
  })).sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  return {
    data,
    total: data.length,
    pendingAckCount: data.filter((a) => a.status !== "ACKED").length,
  };
}

export function getDemoMeAnnouncement(id: string): MeAnnouncementItem {
  const row = DEMO_ANNOUNCEMENTS.find((a) => a.id === id);
  if (!row) throw new Error("ANNOUNCEMENT_NOT_FOUND");
  if (!row.readAt) row.readAt = new Date().toISOString();
  row.status = statusOf(row);
  return { ...row };
}

export function ackDemoMeAnnouncement(id: string): MeAnnouncementItem {
  const row = DEMO_ANNOUNCEMENTS.find((a) => a.id === id);
  if (!row) throw new Error("ANNOUNCEMENT_NOT_FOUND");
  const now = new Date().toISOString();
  if (!row.readAt) row.readAt = now;
  if (!row.ackedAt) row.ackedAt = now;
  row.status = statusOf(row);
  return { ...row };
}

let demoTimecardCapability = true;

const DEMO_TIMECARDS: MeTimecardListResponse["data"] = [
  {
    id: "tc-2026-08-12",
    date: "2026-08-12",
    punches: [
      { at: "2026-08-12T07:58:00.000-03:00", type: "IN" },
      { at: "2026-08-12T12:00:00.000-03:00", type: "OUT" },
      { at: "2026-08-12T13:00:00.000-03:00", type: "IN" },
      { at: "2026-08-12T17:00:00.000-03:00", type: "OUT" },
    ],
    occurrences: [],
    syncedAt: "2026-08-12T08:00:00.000Z",
  },
  {
    id: "tc-2026-08-11",
    date: "2026-08-11",
    punches: [
      { at: "2026-08-11T08:02:00.000-03:00", type: "IN" },
      { at: "2026-08-11T12:01:00.000-03:00", type: "OUT" },
      { at: "2026-08-11T13:00:00.000-03:00", type: "IN" },
      { at: "2026-08-11T17:05:00.000-03:00", type: "OUT" },
    ],
    occurrences: [{ code: "ATRASO", label: "Atraso na entrada" }],
    syncedAt: "2026-08-12T08:00:00.000Z",
  },
];

export function setDemoTimecardCapability(available: boolean): void {
  demoTimecardCapability = available;
}

export function listDemoMeTimecards(
  query: MeTimecardListQuery = {},
): MeTimecardListResponse {
  const today = new Date().toISOString().slice(0, 10);
  const period = {
    from: query.from ?? `${today.slice(0, 7)}-01`,
    to: query.to ?? today,
  };
  if (!demoTimecardCapability) {
    return { data: [], total: 0, period, capabilityAvailable: false };
  }
  const data = DEMO_TIMECARDS.filter(
    (d) => d.date >= period.from && d.date <= period.to,
  ).map((d) => ({
    ...d,
    punches: d.punches.map((p) => ({ ...p })),
    occurrences: d.occurrences.map((o) => ({ ...o })),
  }));
  return { data, total: data.length, period, capabilityAvailable: true };
}

export function getDemoMeTimecardBalance(): MeTimecardBalance {
  if (!demoTimecardCapability) {
    return {
      balanceMinutes: 0,
      unit: "MINUTES",
      syncedAt: null,
      capabilityAvailable: false,
    };
  }
  return {
    balanceMinutes: 96,
    unit: "MINUTES",
    syncedAt: "2026-08-12T08:00:00.000Z",
    capabilityAvailable: true,
  };
}
